<template>
  <div v-if="show" class="modal fade show d-block" tabindex="-1" role="dialog" aria-labelledby="signatureModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="signatureModalLabel">
            {{selectedUser.signature ? 'Update' : 'Upload'}} Signature - {{selectedUser.names}}
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Current Signature Display -->
          <div v-if="selectedUser.signature" class="mb-4">
            <label class="form-label">Current Signature:</label>
            <div class="current-signature-container">
              <img :src="getSignatureUrl(selectedUser.signature)"
                   alt="Current signature"
                   class="current-signature-img"
                   @error="handleImageError">
              <div class="signature-overlay">
                <button type="button" class="btn btn-sm btn-danger" @click="removeSignature">
                  <i class="mdi mdi-delete"></i> Remove
                </button>
              </div>
            </div>
          </div>

          <!-- Upload Methods -->
          <div class="row">
            <!-- Method 1: File Upload -->
            <div class="col-md-6 mb-4">
              <div class="upload-method-card">
                <h6 class="mb-3">
                  <i class="mdi mdi-upload text-primary"></i> Upload Image File
                </h6>
                <div class="upload-area" @drop="handleDrop" @dragover.prevent @dragenter.prevent>
                  <input type="file"
                         ref="fileInput"
                         @change="handleFileSelect"
                         accept="image/*"
                         class="d-none">
                  <div class="upload-content" @click="$refs.fileInput.click()">
                    <div v-if="!uploadedFile">
                      <i class="mdi mdi-cloud-upload text-muted" style="font-size: 48px;"></i>
                      <p class="text-muted mb-0">Click to upload or drag & drop</p>
                      <small class="text-muted">PNG, JPG, JPEG (Max: 2MB)</small>
                    </div>
                    <div v-else class="uploaded-preview">
                      <img :src="uploadedFileUrl" alt="Preview" class="preview-img">
                      <p class="mb-0 text-success">{{uploadedFile.name}}</p>
                      <button type="button" class="btn btn-sm btn-outline-danger mt-2" @click.stop="clearUpload">
                        <i class="mdi mdi-close"></i> Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Method 2: Digital Signature Canvas -->
            <div class="col-md-6 mb-4">
              <div class="upload-method-card">
                <h6 class="mb-3">
                  <i class="mdi mdi-draw text-primary"></i> Draw Signature
                </h6>
                <div class="signature-canvas-container">
                  <canvas ref="signatureCanvas"
                          width="300"
                          height="150"
                          class="signature-canvas"
                          @mousedown="startDrawing"
                          @mousemove="draw"
                          @mouseup="stopDrawing"
                          @mouseleave="stopDrawing"
                          @touchstart="startDrawing"
                          @touchmove="draw"
                          @touchend="stopDrawing">
                  </canvas>
                  <div class="canvas-controls mt-2">
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearCanvas">
                      <i class="mdi mdi-eraser"></i> Clear
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-primary" @click="saveCanvasSignature">
                      <i class="mdi mdi-content-save"></i> Use This
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Upload Progress -->
          <div v-if="uploadProgress > 0 && uploadProgress < 100" class="mb-3">
            <div class="progress">
              <div class="progress-bar"
                   role="progressbar"
                   :style="{width: uploadProgress + '%'}"
                   :aria-valuenow="uploadProgress"
                   aria-valuemin="0"
                   aria-valuemax="100">
                {{uploadProgress}}%
              </div>
            </div>
          </div>

          <!-- Error Display -->
          <div v-if="error" class="alert alert-danger" role="alert">
            <i class="mdi mdi-alert-circle me-2"></i>{{error}}
          </div>

          <!-- Success Display -->
          <div v-if="success" class="alert alert-success" role="alert">
            <i class="mdi mdi-check-circle me-2"></i>{{success}}
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>
          <button type="button"
                  class="btn btn-primary"
                  @click="handleSave"
                  :disabled="!hasSignatureToSave || uploading">
            <span v-if="uploading" class="spinner-border spinner-border-sm me-2"></span>
            {{uploading ? 'Uploading...' : (selectedUser.signature ? 'Update' : 'Upload') + ' Signature'}}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>
export default {
  name: "SignatureModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedUser: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      uploadedFile: null,
      uploadedFileUrl: null,
      canvasSignature: null,
      isDrawing: false,
      uploadProgress: 0,
      uploading: false,
      error: null,
      success: null,
      // Canvas drawing variables
      lastX: 0,
      lastY: 0
    }
  },
  computed: {
    hasSignatureToSave() {
      return this.uploadedFile || this.canvasSignature;
    }
  },
  watch: {
    show(newVal) {
      if (!newVal) {
        this.resetModal();
      } else {
        this.$nextTick(() => {
          this.initializeCanvas();
        });
      }
    }
  },
  methods: {
    // File upload methods
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
    },

    handleDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    processFile(file) {
      // Validate file
      if (!this.validateFile(file)) {
        return;
      }

      this.uploadedFile = file;
      this.uploadedFileUrl = URL.createObjectURL(file);
      this.canvasSignature = null; // Clear canvas signature if file is selected
      this.error = null;
    },

    validateFile(file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        this.error = 'Please select an image file (PNG, JPG, JPEG)';
        return false;
      }

      // Check file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        this.error = 'File size must be less than 2MB';
        return false;
      }

      return true;
    },

    clearUpload() {
      this.uploadedFile = null;
      if (this.uploadedFileUrl) {
        URL.revokeObjectURL(this.uploadedFileUrl);
        this.uploadedFileUrl = null;
      }
      this.$refs.fileInput.value = '';
    },

    // Canvas drawing methods
    initializeCanvas() {
      if (this.$refs.signatureCanvas) {
        const canvas = this.$refs.signatureCanvas;
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Set white background
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    },

    getCanvasPosition(event) {
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      if (event.touches) {
        return {
          x: (event.touches[0].clientX - rect.left) * scaleX,
          y: (event.touches[0].clientY - rect.top) * scaleY
        };
      } else {
        return {
          x: (event.clientX - rect.left) * scaleX,
          y: (event.clientY - rect.top) * scaleY
        };
      }
    },

    startDrawing(event) {
      event.preventDefault();
      this.isDrawing = true;
      const pos = this.getCanvasPosition(event);
      this.lastX = pos.x;
      this.lastY = pos.y;
      this.uploadedFile = null; // Clear uploaded file if drawing
      this.clearUpload();
    },

    draw(event) {
      if (!this.isDrawing) return;
      event.preventDefault();

      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext('2d');
      const pos = this.getCanvasPosition(event);

      ctx.beginPath();
      ctx.moveTo(this.lastX, this.lastY);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();

      this.lastX = pos.x;
      this.lastY = pos.y;
    },

    stopDrawing() {
      this.isDrawing = false;
    },

    clearCanvas() {
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      this.canvasSignature = null;
    },

    saveCanvasSignature() {
      const canvas = this.$refs.signatureCanvas;

      // Check if canvas has any drawing
      if (this.isCanvasEmpty()) {
        this.error = 'Please draw a signature first';
        return;
      }

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        this.canvasSignature = blob;
        this.error = null;
        this.success = 'Signature captured successfully!';
        setTimeout(() => this.success = null, 3000);
      }, 'image/png');
    },

    isCanvasEmpty() {
      const canvas = this.$refs.signatureCanvas;
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // Check if all pixels are white (empty canvas)
      for (let i = 0; i < imageData.data.length; i += 4) {
        if (imageData.data[i] !== 255 ||
            imageData.data[i + 1] !== 255 ||
            imageData.data[i + 2] !== 255) {
          return false;
        }
      }
      return true;
    },

    // Signature management methods
    getSignatureUrl(signature) {
      // Adjust this based on your backend signature storage
      if (signature.startsWith('http')) {
        return signature;
      }
      return `./assets/signatures/${signature}`;
    },

    handleImageError(event) {
      event.target.src = './assets/images/signature-placeholder.png';
    },

    removeSignature() {
      const confirmed = window.confirm('Are you sure you want to remove this signature?');
      if (confirmed) {
        this.$emit('remove-signature', this.selectedUser);
      }
    },

    // Save signature
    async handleSave() {
      if (!this.hasSignatureToSave) {
        this.error = 'Please upload a file or draw a signature';
        return;
      }

      this.uploading = true;
      this.uploadProgress = 0;
      this.error = null;

      try {
        const signatureData = new FormData();

        // Add the signature file
        if (this.uploadedFile) {
          signatureData.append('signature', this.uploadedFile);
        } else if (this.canvasSignature) {
          signatureData.append('signature', this.canvasSignature, 'signature.png');
        }

        // Add user data
        signatureData.append('function', 'updateUserSignature');
        signatureData.append('userId', this.selectedUser.id);
        signatureData.append('idNo', this.selectedUser.idNo);
        signatureData.append('names', this.selectedUser.names);
        signatureData.append('sessionNames', sessionStorage.getItem('sessionNames'));
        signatureData.append('sessionIdNo', sessionStorage.getItem('sessionIdNo'));

        // Simulate upload progress (you can replace this with actual progress tracking)
        const progressInterval = setInterval(() => {
          this.uploadProgress += 10;
          if (this.uploadProgress >= 90) {
            clearInterval(progressInterval);
          }
        }, 200);

        this.$emit('upload-signature', signatureData);

        this.uploadProgress = 100;
        this.success = 'Signature uploaded successfully!';

        setTimeout(() => {
          this.uploading = false;
          this.$emit('close');
        }, 1500);

      } catch (error) {
        this.uploading = false;
        this.uploadProgress = 0;
        this.error = 'Failed to upload signature. Please try again.';
        console.error('Signature upload error:', error);
      }
    },

    resetModal() {
      this.clearUpload();
      this.clearCanvas();
      this.canvasSignature = null;
      this.uploadProgress = 0;
      this.uploading = false;
      this.error = null;
      this.success = null;
    }
  }
}
</script>

<style scoped>
.upload-method-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  height: 100%;
  transition: all 0.3s ease;
}

.upload-method-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area:hover {
  border-color: #3b82f6;
  background-color: #f8fafc;
}

.upload-area.drag-over {
  border-color: #3b82f6;
  background-color: #eff6ff;
}

.upload-content {
  width: 100%;
}

.preview-img {
  max-width: 100%;
  max-height: 100px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.signature-canvas-container {
  text-align: center;
}

.signature-canvas {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: crosshair;
  background-color: #fff;
  width: 100%;
  max-width: 300px;
  height: 150px;
}

.canvas-controls {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.current-signature-container {
  position: relative;
  display: inline-block;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  background-color: #f8fafc;
}

.current-signature-img {
  max-width: 200px;
  max-height: 100px;
  object-fit: contain;
  display: block;
}

.signature-overlay {
  position: absolute;
  top: 5px;
  right: 5px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.current-signature-container:hover .signature-overlay {
  opacity: 1;
}

.uploaded-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

/* Loading spinner for upload */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Responsive design */
@media (max-width: 768px) {
  .signature-canvas {
    width: 100%;
    height: 120px;
  }

  .upload-method-card {
    margin-bottom: 20px;
  }

  .current-signature-img {
    max-width: 150px;
    max-height: 80px;
  }
}

/* Touch device improvements */
@media (pointer: coarse) {
  .signature-canvas {
    touch-action: none;
  }
}
</style>